import { ActionIcon, Loader, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Check, LayoutGrid, Lightbulb, PanelLeftClose, PanelLeftOpen } from "lucide-react";

import type { AdCopy } from "../../../api/generated/model";
import { SubmitSearch } from "../../../ui/SubmitSearch";
import classes from "./ad-copies.module.css";
import { sectionsOf } from "./elements";
import { MAX_COMBINATIONS, type TilePicks } from "./picks";

export interface CopyListProps {
  copies: readonly AdCopy[];
  loading: boolean;
  search: string;
  onSearch: (search: string) => void;
  /** The combinations ticked on every tile, out of live's 100. */
  total: number;
  chosenId: string | null;
  onChoose: (copy: AdCopy) => void;
  /** The active tile: its ticks mark the rows, and its reference the copies written from it. */
  tile: { id: string; picks: TilePicks | undefined };
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

/**
 * Live's copies list in the Ad copies modal (`saved-copies-tab/copies-list`, captures
 * advanced-234/12, 14 and 20): "Ad copies" with its fold, live's search, "{n} / 100 selected", and
 * a row per copy. Folded, it keeps each row's number with its title in a tooltip.
 */
export function CopyList(props: CopyListProps) {
  const {
    copies,
    loading,
    search,
    onSearch,
    total,
    chosenId,
    onChoose,
    tile,
    collapsed,
    onCollapse,
  } = props;
  return (
    <div className={classes.list} data-collapsed={collapsed || undefined}>
      <div className={classes.listHeader}>
        {collapsed ? null : <Text className={classes.listTitle}>Ad copies</Text>}
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label={collapsed ? "Open the Ad copies" : "Fold the Ad copies"}
          onClick={() => onCollapse(!collapsed)}
        >
          {collapsed ? (
            <PanelLeftOpen size={20} aria-hidden />
          ) : (
            <PanelLeftClose size={20} aria-hidden />
          )}
        </ActionIcon>
      </div>
      {collapsed ? null : (
        <>
          <SubmitSearch
            label="Search by title"
            value={search}
            onSearch={onSearch}
            className={classes.search}
          />
          <Text className={classes.selected} data-full={total >= MAX_COMBINATIONS || undefined}>
            {total} / {MAX_COMBINATIONS} selected
          </Text>
        </>
      )}
      {loading ? <Loader size="sm" aria-label="Loading the Ad copies" /> : null}
      {!loading && copies.length === 0 && !collapsed ? (
        <Text className={classes.empty}>No Ad copy found</Text>
      ) : null}
      <div className={classes.rows}>
        {copies.map((copy, at) => (
          <Row
            key={copy.id}
            copy={copy}
            number={at + 1}
            chosen={copy.id === chosenId}
            ticked={tile.picks?.[copy.id] !== undefined}
            fromTile={copy.reference?.kind === "asset" && copy.reference.asset_id === tile.id}
            collapsed={collapsed}
            onChoose={() => onChoose(copy)}
          />
        ))}
      </div>
    </div>
  );
}

interface RowProps {
  copy: AdCopy;
  number: number;
  chosen: boolean;
  ticked: boolean;
  fromTile: boolean;
  collapsed: boolean;
  onChoose: () => void;
}

/** A copy's row (live's `text-copy-item`): its number or a check, title, lightbulb and sections. */
function Row({ copy, number, chosen, ticked, fromTile, collapsed, onChoose }: RowProps) {
  const sections = sectionsOf(copy.text_elements).length;
  return (
    <Tooltip label={`${copy.name}, ${sections}`} position="right" withArrow disabled={!collapsed}>
      <UnstyledButton
        className={classes.row}
        aria-current={chosen}
        aria-label={copy.name}
        onClick={onChoose}
      >
        <span className={classes.number}>
          {ticked ? <Check size={14} strokeWidth={1.5} aria-label="Ticked" /> : number}
        </span>
        {collapsed ? null : (
          <>
            <span className={classes.rowTitle}>{copy.name}</span>
            {fromTile ? (
              <Lightbulb size={16} strokeWidth={1.5} aria-label="Written from this reference" />
            ) : null}
            {copy.status === "in_progress" ? <Loader size="xs" aria-label="Generating" /> : null}
            {copy.status === "completed" ? (
              <span className={classes.sections}>
                <LayoutGrid size={16} aria-hidden />
                {sections}
              </span>
            ) : null}
          </>
        )}
      </UnstyledButton>
    </Tooltip>
  );
}
